import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowRight, RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { CinematicHero, CinematicChapter, CinematicMontage } from "./Cinematic";
import { EXPLORE_SLUGS } from "./ExploreTopic";
import { FOUNDATIONS } from "../config/foundations";
import { useAudio } from "../context/AudioContext";
import { usePreviewMode } from "../hooks/usePreviewMode";
import { previewHeaders } from "../lib/previewAuth";
import { FeaturedPerspectiveCard } from "./perspectives/FeaturedPerspectiveCard";

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const { setScene } = useAudio();
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [latestPerspective, setLatestPerspective] = useState(null);
  const [latestFounderFeature, setLatestFounderFeature] = useState(null);
  const previewMode = usePreviewMode();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState({ type: null, message: "" });
  const [submittingNewsletter, setSubmittingNewsletter] = useState(false);
  const containerRef = useRef(null);
  const [searchParams] = useSearchParams();
  const oceanAlt = searchParams.get("ocean") === "alt";
  const oceanFilm = oceanAlt ? "/video/ocean-alt-web.mp4" : "/video/ocean-cove-web.mp4";
  const oceanPoster = oceanAlt ? "/video/ocean-alt-poster.jpg" : "/video/ocean-cove-poster.jpg";

  // The homepage carries the ocean soundscape as its home mood. Only the
  // Chapter Three montage overrides it while it is in view; otherwise ocean
  // remains the ambient bed. The forest soundscape is not used on the home.
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const ch3 = document.querySelector('[data-testid="cinematic-chapter-sky"]');
    const vis = { ch3: false };
    const resolve = () => setScene(vis.ch3 ? "chapter3" : "ocean");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const on = e.isIntersecting && e.intersectionRatio >= 0.5;
        if (e.target === ch3) vis.ch3 = on;
      });
      resolve();
    }, { threshold: [0, 0.5, 1] });
    if (ch3) io.observe(ch3);
    return () => { io.disconnect(); };
  }, [setScene]);

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const sections = root.querySelectorAll('section:not([data-testid^="cinematic"])');
    sections.forEach((s) => s.classList.add("tl-reveal"));
    if (!("IntersectionObserver" in window)) { sections.forEach((s) => s.classList.add("tl-reveal--in")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("tl-reveal--in"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoadingArticles(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const response = await axios.get(`${backendUrl}/api/articles?lang=${lang}`);
        setArticles(response.data);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoadingArticles(false);
      }
    };
    fetchArticles();
  }, [lang]);

  // Latest Perspective — shown on the homepage only when a Perspective is
  // published, or when the editor is in preview mode reviewing a draft.
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${backendUrl}/api/perspectives?lang=${lang}${previewMode ? "&include_drafts=true" : ""}`;
    axios.get(url, { headers: previewMode ? previewHeaders() : {} })
      .then((res) => setLatestPerspective((res.data && res.data[0]) || null))
      .catch(() => setLatestPerspective(null));
  }, [lang, previewMode]);

  // Latest Founder Feature — a discreet discovery point for the Community section.
  // Only surfaces when a Founder Feature is published (or the editor is in preview).
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${backendUrl}/api/community?lang=${lang}${previewMode ? "&include_drafts=true" : ""}`;
    axios.get(url, { headers: previewMode ? previewHeaders() : {} })
      .then((res) => setLatestFounderFeature((res.data && res.data[0]) || null))
      .catch(() => setLatestFounderFeature(null));
  }, [lang, previewMode]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      setNewsletterStatus({ type: "error", message: t("home.newsletterInvalid") });
      return;
    }
    setSubmittingNewsletter(true);
    setNewsletterStatus({ type: null, message: "" });
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      // Editorial attribution: if the visitor is on an editorial page when
      // they subscribe, forward the page path so the report can attribute
      // the conversion. `ref` and `collaborator` are optional and only
      // populated when we can safely derive them from the URL — no PII.
      const path = (typeof window !== "undefined" && window.location) ? window.location.pathname : "/";
      const editorialMatch = path.match(/^\/community\/([^/]+)/);
      const payload = { email: newsletterEmail, page: path };
      if (editorialMatch) {
        payload.ref = editorialMatch[1];
      }
      const response = await axios.post(`${backendUrl}/api/newsletter`, payload);
      // Only claim success if the server confirms the record was stored.
      if (response.data && response.data.success && response.data.stored) {
        const message = response.data.already_subscribed
          ? t("home.newsletterAlready")
          : t("home.newsletterSuccess");
        setNewsletterStatus({ type: "success", message });
        setNewsletterEmail("");
      } else {
        setNewsletterStatus({ type: "error", message: t("home.newsletterError") });
      }
    } catch (err) {
      // If the backend returned a specific detail, surface it; otherwise
      // fall back to the generic error copy.
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setNewsletterStatus({ type: "error", message: detail || t("home.newsletterError") });
    } finally {
      setSubmittingNewsletter(false);
    }
  };

  const foundations = t("home.foundations", { returnObjects: true }) || [];
  const themes = t("home.themes", { returnObjects: true }) || [];
  const storyExamples = t("home.storiesExamples", { returnObjects: true }) || [];
  const marquee = t("home.marquee", { returnObjects: true }) || [];
  const A = "https://customer-assets.emergentagent.com/job_human-flourish-hub/artifacts/";
  const zenGardenHero = "/images/stories-of-flourishing-hero.jpg";
  // Chapter Three montage — a curated collection of authentic editorial photography.
  const MONTAGE_SLIDES = [
    "https://images.unsplash.com/photo-1768345757674-fb0dcc36a6fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    "https://images.unsplash.com/photo-1531969179221-3946e6b5a5e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    "https://static.prod-images.emergentagent.com/jobs/3a44dbe4-fc04-4b1f-83d3-3df6492eba1e/images/3dcf5b84a46909f7262e6a6af0927d35ad5b25a334b659a386c7048df99dbc1f.png",
    "https://images.unsplash.com/photo-1546214755-c5d22447b43b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    "https://images.unsplash.com/photo-1610238115511-81be15284155?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    "https://images.unsplash.com/photo-1770655536319-09ec197274f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    "https://images.pexels.com/photos/32041165/pexels-photo-32041165.jpeg?auto=compress&cs=tinysrgb&w=2000",
    "https://images.unsplash.com/photo-1759097247817-7611910ae819?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  ];

  return (
    <div ref={containerRef} className="space-y-24 md:space-y-32">

      {/* Homepage opening — Ocean film (bright, calm, fluid, quietly uplifting).
          No chapter label. Sound is user-controlled via the ambient toggle. */}
      <CinematicChapter
        testid="cinematic-chapter-ocean"
        film={oceanFilm}
        poster={oceanPoster}
        title={<>To move with life,<br /><span className="italic">not against it.</span></>}
        subtitle={t("home.oceanSubtitle")}
        intention="FLOW · RESILIENCE · EXPLORATION · EMBRACING CHANGE"
        align="center"
        overlay="bg-gradient-to-b from-black/25 via-black/10 to-black/40"
        height="h-[100svh]"
      />

      {/* Intro band */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-8 text-center">
        <span className="text-[10px] sm:text-xs font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block mb-6">
          {t("home.introEyebrow")}
        </span>
        <p className="font-serif text-lg sm:text-2xl text-secondary-custom leading-relaxed font-light">
          {t("home.introBody")}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-10">
          <Link to="/start-here" data-testid="hero-start-cta" className="flex items-center justify-center space-x-2 border border-primary bg-primary-custom text-primary-custom hover:bg-[var(--accent-bronze)] hover:border-[var(--accent-bronze)] hover:text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-500">
            <span>{t("home.startHere")}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/about" data-testid="hero-about-cta" className="flex items-center justify-center space-x-2 border border-subtle-custom bg-surface-custom/50 hover:bg-surface-custom px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-500">
            <span>{t("home.ourStory")}</span>
          </Link>
        </div>
      </section>

      {/* Editorial Ribbon (Infinite Marquee) */}
      <section className="py-4 border-y border-subtle-custom bg-surface-custom overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex items-center space-x-12">
          {marquee.concat(marquee).map((topic, i) => (
            <React.Fragment key={i}>
              <span className="text-[10px] font-sans tracking-[0.3em] text-primary-custom uppercase font-semibold">{topic}</span>
              <span className="text-[var(--accent-bronze)] font-serif text-sm">✦</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* The Mission */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 space-y-4">
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.missionEyebrow")}</span>
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-primary-custom leading-tight">{t("home.missionHeading")}</h2>
          </div>
          <div className="lg:col-span-8 space-y-8 font-serif text-base sm:text-lg text-secondary-custom leading-relaxed font-light">
            <p>{t("home.missionP1")}</p>
            <p>{t("home.missionP2")}</p>
            <div className="pt-4">
              <Link to="/about" className="text-xs font-sans tracking-[0.2em] uppercase text-primary-custom font-semibold hover:text-[var(--accent-bronze)] transition-colors inline-flex items-center space-x-2 border-b border-primary pb-1" data-testid="mission-read-more">
                <span>{t("home.missionLink")}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-subtle-custom" data-testid="home-founder-portrait">
              <img src="/images/home-founder-portrait-japan.jpg" alt="Cristina Lima" loading="lazy" className="w-full h-full object-cover object-top" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.founderEyebrow")}</span>
            <blockquote className="font-serif text-2xl sm:text-3xl italic text-primary-custom leading-snug">&quot;{t("home.founderQuote")}&quot;</blockquote>
            <p className="font-serif text-base sm:text-lg text-secondary-custom leading-relaxed font-light">{t("home.founderP1")}</p>
            <p className="font-serif text-base sm:text-lg text-secondary-custom leading-relaxed font-light">{t("home.founderP2")}</p>
            <div className="pt-2">
              <Link to="/about" data-testid="home-founder-link" className="text-xs font-sans tracking-[0.2em] uppercase text-primary-custom font-semibold hover:text-[var(--accent-bronze)] transition-colors inline-flex items-center space-x-2 border-b border-primary pb-1">
                <span>{t("home.founderLink")}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Six Foundations */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="space-y-16">
          <div className="text-center max-w-2xl mx-auto space-y-4">
            <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.foundationsEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-primary-custom">{t("home.foundationsHeading")}</h2>
            <p className="font-serif text-sm italic text-secondary-custom leading-relaxed">{t("home.foundationsSubtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {foundations.map((foundation, idx) => (
              <div key={idx} className={`p-10 rounded-none transition-all duration-500 flex flex-col justify-between hover:border-[var(--accent-bronze)] ${idx % 2 === 0 ? "bg-surface-custom" : "bg-primary-custom border border-subtle-custom"}`}>
                <div className="space-y-6">
                  <div className="text-[var(--accent-bronze)] font-serif text-2xl italic tracking-wide">{String(idx + 1).padStart(2, '0')}.</div>
                  <h3 className="text-xl font-serif font-medium text-primary-custom">{foundation.title}</h3>
                  <p className="font-sans text-xs sm:text-sm text-secondary-custom leading-relaxed">{foundation.desc}</p>
                </div>
                <div className="pt-8">
                  <Link to={`/foundations/${FOUNDATIONS[idx] ? FOUNDATIONS[idx].slug : ""}`} data-testid={`home-foundation-link-${idx}`} className="text-[10px] font-sans tracking-[0.15em] uppercase text-primary-custom hover:text-[var(--accent-bronze)] transition-colors flex items-center space-x-2">
                    <span>{t("home.foundationsExplore")}</span>
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Philosophy — a quiet manifesto between the six foundations and the
          cinematic chapters. Restrained typography, no ornament, no cards. It
          exists to answer why the site is arranged the way it is: as an
          invitation to self-directed discovery, not a prescribed path. */}
      <section
        className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center"
        data-testid="home-philosophy"
        aria-labelledby="home-philosophy-heading"
      >
        <span className="text-[10px] font-sans tracking-[0.4em] uppercase text-[var(--accent-bronze)] font-semibold block">
          {t("home.philosophyEyebrow")}
        </span>
        <h2
          id="home-philosophy-heading"
          className="mt-6 text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight text-primary-custom leading-tight"
        >
          {t("home.philosophyHeading")}
        </h2>

        <p className="mt-10 font-serif text-xl md:text-2xl italic text-primary-custom leading-relaxed font-light">
          {t("home.philosophyOpening")}
        </p>

        <div className="mt-10 space-y-6 max-w-2xl mx-auto">
          {(t("home.philosophyBody", { returnObjects: true }) || []).map((para, i) => (
            <p key={i} className="font-serif text-base md:text-lg text-secondary-custom leading-relaxed font-light">
              {para}
            </p>
          ))}
        </div>

        <div className="mt-14 pt-10 border-t border-subtle-custom max-w-2xl mx-auto">
          <p className="font-serif text-base md:text-lg italic text-primary-custom leading-relaxed">
            {t("home.philosophyCoda")}
          </p>
        </div>
      </section>

      {/* What We Explore */}
      <section className="bg-surface-custom py-24 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16">
          <div className="text-left max-w-3xl space-y-4">
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.exploreEyebrow")}</span>
            <h2 className="text-3xl md:text-5xl font-serif tracking-tight text-primary-custom">{t("home.exploreHeading")}</h2>
            <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("home.exploreSubtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {themes.map((theme, i) => (
              <Link
                key={EXPLORE_SLUGS[i] || i}
                to={`/explore/${EXPLORE_SLUGS[i] || ""}`}
                data-testid={`explore-topic-link-${i}`}
                className="group bg-primary-custom border border-subtle-custom p-8 rounded-none flex flex-col justify-between hover:border-[var(--accent-bronze)] transition-all duration-500"
              >
                <div className="space-y-4">
                  <div className="w-2 h-2 bg-[var(--accent-bronze)] rounded-none" />
                  <h4 className="text-lg font-serif font-medium text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors duration-300">{theme.title}</h4>
                  <p className="font-sans text-xs text-secondary-custom leading-relaxed">{theme.desc}</p>
                </div>
                <div className="pt-6 border-t border-subtle-custom mt-6 flex justify-between items-center text-[10px] font-sans tracking-wider uppercase text-secondary-custom">
                  <span>{t("home.themeLabel")}</span>
                  <span className="inline-flex items-center gap-1.5">{String(i + 1).padStart(2, '0')}<ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stories of Flourishing — a signature community editorial feature */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="stories-of-flourishing">
        <div className="border border-subtle-custom bg-surface-custom/20 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative min-h-[320px] lg:min-h-full overflow-hidden">
              <img src={zenGardenHero} alt={t("home.storiesHeading")} loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-testid="stories-hero-image" />
            </div>
            <div className="p-8 md:p-14 flex flex-col justify-center space-y-6">
              <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.storiesEyebrow")}</span>
              <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-primary-custom leading-tight">{t("home.storiesHeading")}</h2>
              <p className="font-serif text-base sm:text-lg text-secondary-custom leading-relaxed font-light">{t("home.storiesSubtitle")}</p>

              {/* First Life Story — a restrained editorial introduction. Not a
                  promotional card and not repeated elsewhere on the site. */}
              <div className="border-l-2 border-[var(--accent-bronze)] pl-5 py-2 space-y-3" data-testid="home-life-story-feature">
                <span className="text-[9px] font-sans tracking-[0.3em] uppercase text-secondary-custom">{t("journal.storyEyebrow")}</span>
                <Link
                  to="/stories/when-luxury-became-ordinary"
                  data-testid="home-life-story-link"
                  className="group block space-y-2"
                >
                  <h3 className="font-serif text-xl md:text-2xl text-primary-custom leading-snug tracking-tight group-hover:text-[var(--accent-bronze)] transition-colors">
                    {t("journal.storyTitle")}
                  </h3>
                  <p className="font-serif text-sm italic text-secondary-custom leading-relaxed">{t("journal.storyExcerpt")}</p>
                  <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold group-hover:text-[var(--accent-bronze)] transition-colors">
                    <span>{t("journal.readStory")}</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </div>

              <div className="pt-2 space-y-3">
                <span className="text-[9px] font-sans tracking-[0.25em] uppercase text-secondary-custom block">{t("home.storiesExamplesLabel")}</span>
                <div className="flex flex-wrap gap-2">
                  {storyExamples.map((ex, i) => (
                    <span key={i} className="text-[10px] font-sans tracking-wide text-secondary-custom border border-subtle-custom bg-primary-custom px-3 py-1 rounded-full">{ex}</span>
                  ))}
                </div>
              </div>

              <p className="text-[10px] font-sans tracking-[0.15em] uppercase text-secondary-custom/70 pt-2">{t("home.storiesNoSocial")}</p>

              {/* Entry point into the Stories of Flourishing collection.
                  Kept as a discreet inline link — the collection lives at
                  /community (Founder Features series). No redesign. */}
              <div className="pt-4">
                <Link
                  to="/community"
                  data-testid="home-stories-collection-link"
                  className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.3em] uppercase text-primary-custom font-semibold border-b border-primary pb-1 hover:text-[var(--accent-bronze)] hover:border-[var(--accent-bronze)] transition-colors"
                >
                  <span>See all Stories of Flourishing</span>
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Feature — a discreet, editorial discovery point for the
          Community section. Not a promotional card. Renders only when a
          Founder Feature is published (or the editor is in preview mode). */}
      {latestFounderFeature && (
        <section
          className="max-w-7xl mx-auto px-6 md:px-12 py-12"
          data-testid="home-founder-feature-module"
        >
          <Link
            to={`/community/${latestFounderFeature.slug}`}
            data-testid="home-founder-feature-link"
            className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center border-t border-b border-subtle-custom py-12"
          >
            {latestFounderFeature.hero?.image && (
              <div className="md:col-span-5">
                <div className="aspect-[4/5] overflow-hidden border border-subtle-custom">
                  <img
                    src={latestFounderFeature.hero.image}
                    alt={latestFounderFeature.hero.alt || latestFounderFeature.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
              </div>
            )}
            <div className={`${latestFounderFeature.hero?.image ? "md:col-span-7" : "md:col-span-12"} space-y-5`}>
              <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold block">
                {[latestFounderFeature.series, latestFounderFeature.seriesNumber].filter(Boolean).join(" · ") || t("home.founderFeatureEyebrow")}
              </span>
              <h2 className="font-serif tracking-tight text-3xl md:text-4xl text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors leading-tight">
                {latestFounderFeature.title}
              </h2>
              {(latestFounderFeature.homeSummary || latestFounderFeature.subtitle) && (
                <p className="font-serif text-base md:text-lg italic text-secondary-custom leading-relaxed font-light">
                  {latestFounderFeature.homeSummary || latestFounderFeature.subtitle}
                </p>
              )}
              {latestFounderFeature.subject?.name && (
                <p className="text-[11px] font-sans tracking-[0.2em] uppercase text-secondary-custom">
                  {latestFounderFeature.subject.name}
                  {latestFounderFeature.subject.role ? ` · ${latestFounderFeature.subject.role}` : ""}
                </p>
              )}
              <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold group-hover:text-[var(--accent-bronze)] transition-colors pt-2">
                <span>{latestFounderFeature.ctaLabel || t("home.founderFeatureCta")}</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </section>
      )}

      {/* Featured Journal Articles */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="space-y-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4">
              <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.journalEyebrow")}</span>
              <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-primary-custom">{t("home.journalHeading")}</h2>
            </div>
            <div>
              <Link to="/journal" className="text-xs font-sans tracking-[0.2em] uppercase text-primary-custom hover:text-[var(--accent-bronze)] transition-colors inline-flex items-center space-x-2 border border-subtle-custom bg-surface-custom px-6 py-3" data-testid="home-all-articles-cta">
                <span>{t("home.readJournal")}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {loadingArticles ? (
            <div className="py-24 text-center text-secondary-custom flex flex-col items-center space-y-4">
              <RefreshCw className="w-6 h-6 animate-spin text-[var(--accent-bronze)]" />
              <span className="font-serif italic text-sm">{t("home.gathering")}</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {articles.slice(0, 3).map((art) => (
                <article key={art.id} className="flex flex-col space-y-6 group border border-subtle-custom p-6 bg-surface-custom/30 hover:border-[var(--accent-bronze)] transition-all duration-500">
                  <div className="aspect-[16/10] overflow-hidden border border-subtle-custom relative">
                    {art.video ? (
                      <video src={art.video} poster={art.image} muted loop playsInline autoPlay={typeof window!=='undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches} preload="metadata" aria-label={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" />
                    ) : (
                      <img
                        src={art.image}
                        alt={art.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                        style={art.image && art.image.includes('student-again-ocean-book') ? { objectPosition: 'center center' } : undefined}
                      />
                    )}
                    {art.credit && (
                      <span data-testid={`home-article-credit-${art.id}`} className="absolute bottom-2 right-2 z-10 text-[9px] font-sans tracking-[0.15em] uppercase text-white/90 bg-black/40 backdrop-blur-[2px] px-2 py-1 pointer-events-none">{art.credit}</span>
                    )}
                  </div>
                  <div className="flex-grow space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 text-[10px] font-sans tracking-wide text-secondary-custom">
                        <span>{art.author}</span>
                      </div>
                      <h3 className="text-xl font-serif tracking-tight text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors">{art.title}</h3>
                      <p className="font-sans text-xs text-secondary-custom leading-relaxed">{art.summary}</p>
                    </div>
                    <div className="pt-4 border-t border-subtle-custom flex justify-between items-center">
                      <Link to={`/journal?article=${art.id}`} className="text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold group-hover:text-[var(--accent-bronze)] transition-colors inline-flex items-center space-x-2" data-testid={`featured-article-link-${art.id}`}>
                        <span>{t("home.readEssay")}</span>
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Latest Perspective — a quiet editorial discovery point. Hidden unless
          a Perspective is published (or the editor is in preview mode). */}
      {latestPerspective && (
        <FeaturedPerspectiveCard
          testId="home-latest-perspective"
          eyebrow={t("perspectives.latest")}
          categoryLabel={latestPerspective.category}
          title={latestPerspective.title}
          subtitle={latestPerspective.subtitle}
          href={`/perspectives/${latestPerspective.slug}`}
          linkLabel={t("perspectives.readPerspective")}
          hero={latestPerspective.hero?.image ? { image: latestPerspective.hero.image, alt: latestPerspective.hero.alt } : null}
          heroPlaceholder={{
            label: t("perspectives.draftHeroLabel"),
            caption: t("perspectives.draftHeroCaption"),
          }}
          isDraft={latestPerspective.status === "draft"}
        />
      )}

      {/* Chapter Three — a cinematic editorial montage: a slow visual essay */}
      <CinematicMontage
        testid="cinematic-chapter-sky"
        slides={MONTAGE_SLIDES}
        captions={t("home.montage", { returnObjects: true }) || []}
        chapter={t("home.skyChapter")}
        title={<>{t("home.skyTitleLead")}<br /><span className="italic">{t("home.skyTitleItalic")}</span></>}
        intention={t("home.skyIntention")}
        endCtaLabel={t("home.montageCtaPrimary")}
        endCtaTo="/world"
        endCtaSecondaryLabel={t("home.montageCtaSecondary")}
        endCtaSecondaryTo="/journal"
      />

      {/* Newsletter Subscription */}
      <section data-testid="newsletter-section" className="relative overflow-hidden bg-surface-custom py-24 transition-colors duration-500 border-t border-subtle-custom">
        <div className="absolute inset-0 z-0 opacity-5">
          <img src="https://images.unsplash.com/photo-1545873681-d8affd67677b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center space-y-12">
          <div className="space-y-4">
            <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("home.newsletterEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-primary-custom">{t("home.newsletterHeading")}</h2>
            <p className="font-serif text-sm italic text-secondary-custom max-w-xl mx-auto leading-relaxed font-light">{t("home.newsletterSubtitle")}</p>
          </div>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto space-y-4" noValidate>
            <div className="sm:flex sm:flex-row sm:items-center sm:border-b sm:border-focus-custom sm:pb-2 sm:gap-0">
              <label htmlFor="newsletter-email" className="block sm:sr-only text-xs tracking-[0.25em] uppercase text-secondary-custom/60 mb-3">{t("home.newsletterPlaceholder")}</label>
              <input id="newsletter-email" type="email" inputMode="email" autoComplete="email" placeholder={t("home.newsletterPlaceholder")} value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} disabled={submittingNewsletter} data-testid="newsletter-email-input" className="w-full min-h-[48px] sm:min-h-0 bg-transparent border-b border-focus-custom sm:border-b-0 outline-none focus:outline-none text-xs tracking-[0.25em] text-primary-custom uppercase placeholder:text-secondary-custom/60 py-3 sm:py-2 sm:px-2" required />
              <div className="mt-6 sm:mt-0 flex justify-center sm:block">
                <button type="submit" disabled={submittingNewsletter} data-testid="newsletter-submit-button" className="w-auto min-h-[48px] sm:min-h-0 text-[10px] uppercase font-sans tracking-[0.2em] font-semibold text-primary-custom hover:text-[var(--accent-bronze)] transition-colors py-3 sm:py-2 px-6 sm:px-4 whitespace-nowrap">
                  {submittingNewsletter ? t("home.newsletterSubmitting") : t("home.newsletterSubscribe")}
                </button>
              </div>
            </div>
            {newsletterStatus.type && (
              <div data-testid="newsletter-status-message" className={`flex items-center justify-center space-x-2 text-xs pt-4 transition-all duration-300 ${newsletterStatus.type === "success" ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"}`}>
                {newsletterStatus.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <span>{newsletterStatus.message}</span>
              </div>
            )}
          </form>
        </div>
      </section>

    </div>
  );
}
